<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-water me-2"></i>{{ $t('page.blood_bank', 'Blood Bank') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stockStats.available || 0 }}</div><div class="hms-stat-lbl">Available Units</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stockStats.quarantine || 0 }}</div><div class="hms-stat-lbl">In Quarantine</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stockStats.expiring_soon || 0 }}</div><div class="hms-stat-lbl">Expiring &lt;7d</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ requestStats.urgent_pending || 0 }}</div><div class="hms-stat-lbl">Urgent Requests</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ logStats.reactions || 0 }}</div><div class="hms-stat-lbl">Reactions (30d)</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='stock' }" @click="tab='stock'"><i class="mdi mdi-package-variant me-1"></i>Stock</button>
        <button class="hms-tab" :class="{ active: tab==='donors' }" @click="tab='donors'"><i class="mdi mdi-account-heart me-1"></i>Donors</button>
        <button class="hms-tab" :class="{ active: tab==='requests' }" @click="tab='requests'"><i class="mdi mdi-clipboard-list me-1"></i>Requests</button>
        <button class="hms-tab" :class="{ active: tab==='log' }" @click="tab='log'"><i class="mdi mdi-history me-1"></i>Issues / Transfusions</button>
      </div>

      <!-- ============ STOCK TAB ============ -->
      <div v-if="tab==='stock'">
        <!-- Per-blood-group threshold alerts (Narok FB#1-20) -->
        <div class="hms-card mb-3" v-if="thresholds && thresholds.groups && thresholds.groups.length">
          <div class="hms-card-head d-flex justify-content-between align-items-center">
            <span><i class="mdi mdi-alert-decagram-outline me-1"></i>Stock Levels &amp; Donation-Drive Alerts</span>
            <span v-if="thresholds.low_count > 0" class="hms-badge hms-badge-danger">{{ thresholds.low_count }} group(s) need a donation drive</span>
            <span v-else class="hms-badge hms-badge-success">All groups above threshold</span>
          </div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>Group</th><th>Available</th><th>Min units</th><th>Status</th><th>Alert email(s)</th><th></th></tr></thead>
              <tbody>
                <tr v-for="g in thresholds.groups" :key="g.blood_group">
                  <td><strong>{{ g.blood_group }}</strong></td>
                  <td class="fw-bold">{{ g.available }}</td>
                  <td><input class="hms-input hms-input-sm" type="number" min="0" max="9999" v-model.number="g.min_units" style="width:90px" /></td>
                  <td><span class="hms-badge" :class="thresholdBadge(g.status)"><i class="mdi" :class="g.status==='critical' ? 'mdi-alert-octagon' : (g.status==='low' ? 'mdi-alert' : 'mdi-check-circle')"></i> {{ g.status === 'critical' ? 'CRITICAL — run drive' : (g.status === 'low' ? 'Low — run drive' : 'OK') }}</span></td>
                  <td><input class="hms-input hms-input-sm" v-model="g.alert_emails" placeholder="comma-separated emails" style="min-width:220px" /></td>
                  <td><button class="hms-btn hms-btn-blue hms-btn-sm" :disabled="savingGroup === g.blood_group" @click="saveThreshold(g)"><i class="mdi mdi-content-save"></i> Save</button></td>
                </tr>
              </tbody>
            </table>
            <div class="hms-muted-sm p-2">Sets when this group is flagged for a donation drive. Recipients (comma-separated) receive a daily alert email; in-system alert is always shown here.</div>
          </div>
        </div>

        <!-- By group/product matrix -->
        <div class="hms-card mb-3">
          <div class="hms-card-head">Available by group + product</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Whole</th><th>PRBC</th><th>FFP</th><th>Platelets</th><th>Cryo</th>
                  <th>Quarantine</th>
                  <th>Expiring&nbsp;7d</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="g in groupMatrix" :key="g.group">
                  <td><strong>{{ g.group }}</strong></td>
                  <td>{{ g.whole_blood || 0 }}</td>
                  <td>{{ g.prbc || 0 }}</td>
                  <td>{{ g.ffp || 0 }}</td>
                  <td>{{ g.platelets || 0 }}</td>
                  <td>{{ g.cryoprecipitate || 0 }}</td>
                  <td>{{ g.quarantine || 0 }}</td>
                  <td :class="g.expiring ? 'st-bad' : ''">{{ g.expiring || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2">
            <select v-model="stockStatusFilter" class="hms-input" @change="loadStock">
              <option value="">All statuses</option>
              <option value="available">Available</option>
              <option value="quarantine">Quarantine</option>
              <option value="reserved">Reserved</option>
              <option value="issued">Issued</option>
              <option value="transfused">Transfused</option>
              <option value="expired">Expired</option>
              <option value="discarded">Discarded</option>
            </select>
            <select v-model="stockGroupFilter" class="hms-input" @change="loadStock">
              <option value="">All groups</option>
              <option v-for="g in bloodGroups" :key="g" :value="g">{{ g }}</option>
            </select>
          </div>
          <button class="hms-btn hms-btn-success" @click="openDonationModal()">
            <i class="mdi mdi-water-plus"></i> Record Donation
          </button>
        </div>

        <table class="hms-table">
          <thead>
            <tr>
              <th>Unit #</th>
              <th>Group</th>
              <th>Product</th>
              <th>Donor</th>
              <th>Collected</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Screening</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in units" :key="u.unit_id" :class="rowClassForUnit(u)">
              <td><strong>{{ u.unit_number }}</strong></td>
              <td><span class="hms-group-pill" :class="'g-' + u.blood_group.replace(/[^A-Z]/g, '').toLowerCase()">{{ u.blood_group }}</span></td>
              <td>{{ formatProduct(u.product_type) }}</td>
              <td>{{ u.donor_name || '—' }} <span class="hms-muted-sm">{{ u.donor_no }}</span></td>
              <td>{{ u.donation_date }}</td>
              <td :class="u.days_to_expiry < 7 ? 'st-bad' : ''">{{ u.expiry_date }} <span class="hms-muted-sm">({{ u.days_to_expiry }}d)</span></td>
              <td><span class="hms-pill" :class="statusPill(u.status)">{{ u.status }}</span></td>
              <td>
                <span class="hms-screen-dot" :class="screenDot(u.hiv_result)"      :title="'HIV: '+u.hiv_result">H</span>
                <span class="hms-screen-dot" :class="screenDot(u.hbsag_result)"    :title="'HBsAg: '+u.hbsag_result">B</span>
                <span class="hms-screen-dot" :class="screenDot(u.hcv_result)"      :title="'HCV: '+u.hcv_result">C</span>
                <span class="hms-screen-dot" :class="screenDot(u.syphilis_result)" :title="'Syphilis: '+u.syphilis_result">S</span>
                <span class="hms-screen-dot" :class="screenDot(u.malaria_result)"  :title="'Malaria: '+u.malaria_result">M</span>
              </td>
              <td>
                <button v-if="u.status === 'quarantine'" class="hms-btn hms-btn-blue hms-btn-sm" @click="openScreenModal(u)">
                  <i class="mdi mdi-test-tube"></i> Screen
                </button>
                <button v-if="u.status !== 'discarded' && u.status !== 'expired' && u.status !== 'transfused'" class="hms-btn-link" @click="discardUnit(u)" title="Discard">
                  <i class="mdi mdi-trash-can"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ DONORS TAB ============ -->
      <div v-if="tab==='donors'">
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2">
            <input v-model="donorSearch" class="hms-input" placeholder="Search donor (name / phone / ID)" @keyup.enter="loadDonors" />
            <select v-model="donorGroupFilter" class="hms-input" @change="loadDonors">
              <option value="">All groups</option>
              <option v-for="g in bloodGroups" :key="g" :value="g">{{ g }}</option>
            </select>
          </div>
          <button class="hms-btn hms-btn-success" @click="openDonorModal()">
            <i class="mdi mdi-account-plus"></i> Register Donor
          </button>
        </div>

        <table class="hms-table">
          <thead>
            <tr>
              <th>Donor #</th><th>Name</th><th>Group</th><th>Type</th><th>Phone</th>
              <th>Donations</th><th>Last donation</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in donors" :key="d.donor_id">
              <td><strong>{{ d.donor_no }}</strong></td>
              <td>{{ d.first_name }} {{ d.last_name }}</td>
              <td><span class="hms-group-pill" :class="'g-' + (d.blood_group||'').replace(/[^A-Z]/g, '').toLowerCase()">{{ d.blood_group }}</span></td>
              <td>{{ d.donor_type }}</td>
              <td>{{ d.phone || '—' }}</td>
              <td>{{ d.total_donations || 0 }}</td>
              <td>{{ d.last_donation_date || '—' }}</td>
              <td>
                <span v-if="d.deferral_status==='permanent'" class="hms-pill hms-pill-danger">Permanently deferred</span>
                <span v-else-if="d.deferral_status==='temporary'" class="hms-pill hms-pill-warn">Deferred until {{ d.deferral_until }}</span>
                <span v-else class="hms-pill hms-pill-success">Eligible</span>
              </td>
              <td>
                <button class="hms-btn-link" @click="openDonorModal(d)" title="Edit"><i class="mdi mdi-pencil"></i></button>
                <button v-if="d.deferral_status === 'none'" class="hms-btn hms-btn-success hms-btn-sm" @click="openDonationModal(d)"><i class="mdi mdi-water-plus"></i> Donate</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ REQUESTS TAB ============ -->
      <div v-if="tab==='requests'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openRequestModal()">
            <i class="mdi mdi-clipboard-plus"></i> New Request
          </button>
        </div>
        <div v-if="!requests.length" class="hms-empty">
          <i class="mdi mdi-clipboard-check"></i><h5>No active requests</h5>
        </div>
        <div v-else class="hms-request-list">
          <div v-for="r in requests" :key="r.request_id" class="hms-request-card" :class="urgencyClass(r.urgency)">
            <div class="hms-request-head">
              <div>
                <strong>{{ r.request_no }}</strong>
                <span class="hms-pill ms-2" :class="urgencyPill(r.urgency)">{{ r.urgency.toUpperCase() }}</span>
              </div>
              <span class="hms-muted-sm">{{ formatDateTime(r.requested_at) }}</span>
            </div>
            <div class="hms-request-body">
              <div><strong>{{ r.patient_name }}</strong> <span class="hms-muted-sm">{{ r.patient_no }}</span></div>
              <div class="hms-muted-sm">{{ r.ward_name || '—' }} · Requested by {{ r.requested_by_name }}</div>
              <div class="mt-2">
                Need <strong>{{ r.units_required }}</strong> {{ formatProduct(r.product_type) }}
                <span class="hms-group-pill" :class="'g-' + (r.blood_group_required||'').replace(/[^A-Z]/g, '').toLowerCase()">{{ r.blood_group_required }}</span>
                <span class="hms-muted-sm ms-2">Allocated: {{ r.units_allocated || 0 }}/{{ r.units_required }}</span>
              </div>
              <div v-if="r.clinical_indication" class="hms-muted-sm mt-1">{{ r.clinical_indication }}</div>
              <div v-if="r.hb_level" class="hms-muted-sm">Hb: {{ r.hb_level }} g/dL</div>
            </div>
            <div class="hms-request-actions">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openCrossmatchModal(r)"><i class="mdi mdi-link-variant"></i> Crossmatch</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ ISSUES / TRANSFUSION LOG ============ -->
      <div v-if="tab==='log'">
        <div class="d-flex justify-content-between mb-2">
          <label><input type="checkbox" v-model="reactionsOnly" @change="loadLog" /> Reactions only</label>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>Issue</th><th>Patient</th><th>Unit</th><th>XM</th>
              <th>Issued</th><th>Transfused</th><th>Status</th><th>Reaction</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="i in issues" :key="i.issue_id" :class="i.reaction_occurred==1 ? 'row-warn' : ''">
              <td>{{ i.request_no }} · #{{ i.issue_id }}</td>
              <td>{{ i.patient_name }} <span class="hms-muted-sm">{{ i.patient_no }}</span></td>
              <td>{{ i.unit_number }} ({{ i.blood_group }} {{ formatProduct(i.product_type) }})</td>
              <td><span class="hms-pill" :class="xmPill(i.crossmatch_result)">{{ i.crossmatch_result }}</span></td>
              <td>{{ i.issued_at ? formatDateTime(i.issued_at) : '—' }}</td>
              <td>{{ i.transfusion_completed_at ? formatDateTime(i.transfusion_completed_at) : '—' }}</td>
              <td><span class="hms-pill" :class="issueStatusPill(i.status)">{{ i.status }}</span></td>
              <td>
                <span v-if="i.reaction_occurred==1" class="hms-pill hms-pill-danger">{{ i.reaction_type }} / {{ i.reaction_severity }}</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <button v-if="i.status === 'crossmatched'" class="hms-btn hms-btn-blue hms-btn-sm" @click="issueUnit(i)"><i class="mdi mdi-truck"></i> Issue</button>
                <button v-if="i.status === 'issued'" class="hms-btn hms-btn-success hms-btn-sm" @click="openTransfuseModal(i)"><i class="mdi mdi-iv-bag"></i> Transfuse</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- =============== DONOR MODAL =============== -->
    <div v-if="donorModal" class="hms-modal-bg" @click.self="donorModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>{{ donor.donor_id ? 'Edit Donor' : 'Register Donor' }}</h5><button class="hms-icon-btn" @click="donorModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">First name *</label><input v-model="donor.first_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Last name *</label><input v-model="donor.last_name" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">National ID</label><input v-model="donor.national_id" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">DOB</label><input v-model="donor.date_of_birth" type="date" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Sex</label>
              <select v-model="donor.sex" class="hms-input"><option value="">—</option><option value="M">M</option><option value="F">F</option></select>
            </div>
            <div class="col-2"><label class="hms-label">Wt (kg)</label><input v-model.number="donor.weight_kg" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Phone</label><input v-model="donor.phone" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Group</label>
              <select v-model="donor.blood_group" class="hms-input">
                <option value="unknown">Unknown</option>
                <option v-for="g in bloodGroups" :key="g" :value="g">{{ g }}</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Type</label>
              <select v-model="donor.donor_type" class="hms-input">
                <option value="voluntary">Voluntary</option><option value="family">Family</option>
                <option value="replacement">Replacement</option><option value="autologous">Autologous</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Address</label><input v-model="donor.address" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Hb (g/dL)</label><input v-model.number="donor.hb_level" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Deferral</label>
              <select v-model="donor.deferral_status" class="hms-input">
                <option value="none">Eligible</option><option value="temporary">Temporary</option><option value="permanent">Permanent</option>
              </select>
            </div>
            <div class="col-4" v-if="donor.deferral_status==='temporary'"><label class="hms-label">Until</label><input v-model="donor.deferral_until" type="date" class="hms-input" /></div>
            <div class="col-12" v-if="donor.deferral_status !== 'none'"><label class="hms-label">Deferral reason</label><input v-model="donor.deferral_reason" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="donor.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="donorModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveDonor">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== DONATION MODAL =============== -->
    <div v-if="donationModal" class="hms-modal-bg" @click.self="donationModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Record Donation</h5><button class="hms-icon-btn" @click="donationModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!donation.donor_id">
            <label class="hms-label">Donor *</label>
            <div class="d-flex gap-2">
              <input v-model="donation.donor_search" class="hms-input" placeholder="Search by name / phone / donor #" @keyup.enter="findDonationDonor" />
              <button class="hms-btn hms-btn-blue" @click="findDonationDonor">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ donation.donor_name }} ({{ donation.donor_group }}) <a href="javascript:void(0)" class="ms-2" @click="donation.donor_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-6"><label class="hms-label">Group *</label>
              <select v-model="donation.blood_group" class="hms-input">
                <option v-for="g in bloodGroups" :key="g" :value="g">{{ g }}</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Product</label>
              <select v-model="donation.product_type" class="hms-input">
                <option value="whole_blood">Whole blood</option>
                <option value="prbc">Packed red cells</option>
                <option value="ffp">FFP</option>
                <option value="platelets">Platelets</option>
                <option value="cryoprecipitate">Cryoprecipitate</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Volume (ml)</label><input v-model.number="donation.volume_ml" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Storage location</label><input v-model="donation.storage_location" class="hms-input" placeholder="Fridge A · shelf 2" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="donationModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !donation.donor_id" @click="saveDonation">{{ saving?'Saving…':'Collect Unit' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== SCREEN MODAL =============== -->
    <div v-if="screenModal" class="hms-modal-bg" @click.self="screenModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>TTI Screening — {{ screenUnitRow.unit_number }}</h5><button class="hms-icon-btn" @click="screenModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-warn-banner">
            <i class="mdi mdi-shield-alert"></i>
            Mandatory TTI screening. Any positive → unit will be auto-discarded.
          </div>
          <div v-for="t in screenTests" :key="t.key" class="d-flex align-items-center gap-2 mb-2">
            <label class="hms-label" style="min-width:120px; margin:0">{{ t.label }}</label>
            <select v-model="screenForm[t.key]" class="hms-input">
              <option value="">—</option>
              <option value="negative">Negative</option>
              <option value="positive">Positive</option>
              <option value="indeterminate">Indeterminate</option>
            </select>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="screenModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveScreening">{{ saving?'Saving…':'Save Results' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== REQUEST MODAL =============== -->
    <div v-if="requestModal" class="hms-modal-bg" @click.self="requestModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Blood Request</h5><button class="hms-icon-btn" @click="requestModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!request.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="request.patient_search" class="hms-input" placeholder="Search patient" @keyup.enter="findRequestPatient" />
              <button class="hms-btn hms-btn-blue" @click="findRequestPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ request.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="request.patient_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">Group needed *</label>
              <select v-model="request.blood_group_required" class="hms-input">
                <option value="">—</option>
                <option v-for="g in bloodGroups" :key="g" :value="g">{{ g }}</option>
                <option value="any">Any (compatible)</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Patient group</label>
              <select v-model="request.patient_blood_group" class="hms-input">
                <option value="unknown">Unknown</option>
                <option v-for="g in bloodGroups" :key="g" :value="g">{{ g }}</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Units</label><input v-model.number="request.units_required" type="number" min="1" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Product</label>
              <select v-model="request.product_type" class="hms-input">
                <option value="whole_blood">Whole blood</option>
                <option value="prbc">Packed red cells</option>
                <option value="ffp">FFP</option>
                <option value="platelets">Platelets</option>
                <option value="cryoprecipitate">Cryoprecipitate</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Urgency</label>
              <select v-model="request.urgency" class="hms-input">
                <option value="routine">Routine</option><option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option><option value="massive">Massive transfusion</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Hb (g/dL)</label><input v-model.number="request.hb_level" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Clinical indication</label><textarea v-model="request.clinical_indication" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="requestModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !request.patient_id" @click="saveRequest">{{ saving?'Saving…':'Submit Request' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== CROSSMATCH MODAL =============== -->
    <div v-if="xmModal" class="hms-modal-bg" @click.self="xmModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head"><h5>Crossmatch — {{ xmRequest.request_no }}</h5><button class="hms-icon-btn" @click="xmModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">
            Recipient group: <strong>{{ xmRecipientGroup }}</strong>.
            Compatible donor groups shown below.
          </div>
          <div v-if="!xmCompatible.length" class="hms-warn-banner">
            <i class="mdi mdi-alert"></i> No compatible units in available stock.
          </div>
          <table v-else class="hms-table">
            <thead><tr><th>Unit #</th><th>Group</th><th>Product</th><th>Donor</th><th>Expires</th><th>XM Result</th><th></th></tr></thead>
            <tbody>
              <tr v-for="u in xmCompatible" :key="u.unit_id">
                <td><strong>{{ u.unit_number }}</strong></td>
                <td><span class="hms-group-pill" :class="'g-' + u.blood_group.replace(/[^A-Z]/g, '').toLowerCase()">{{ u.blood_group }}</span></td>
                <td>{{ formatProduct(u.product_type) }}</td>
                <td>{{ u.donor_name }}</td>
                <td>{{ u.expiry_date }} <span class="hms-muted-sm">({{ u.days_to_expiry }}d)</span></td>
                <td>
                  <select v-model="xmResults[u.unit_id]" class="hms-input">
                    <option value="">—</option>
                    <option value="compatible">Compatible</option>
                    <option value="incompatible">Incompatible</option>
                    <option value="warm">Warm reaction</option>
                    <option value="cold">Cold reaction</option>
                  </select>
                </td>
                <td>
                  <button class="hms-btn hms-btn-success hms-btn-sm" :disabled="!xmResults[u.unit_id]" @click="confirmCrossmatch(u)">
                    <i class="mdi mdi-link-variant"></i> Reserve
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="xmModal=false">Close</button>
        </div>
      </div>
    </div>

    <!-- =============== TRANSFUSE MODAL =============== -->
    <div v-if="txModal" class="hms-modal-bg" @click.self="txModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>Record Transfusion — Unit {{ tx.unit_number }}</h5><button class="hms-icon-btn" @click="txModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Started</label><input v-model="tx.started_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Completed</label><input v-model="tx.completed_at" type="datetime-local" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Volume transfused (ml)</label><input v-model.number="tx.volume_transfused_ml" type="number" class="hms-input" /></div>
            <div class="col-6 d-flex align-items-end">
              <label><input type="checkbox" v-model="tx.reaction_occurred" :true-value="1" :false-value="0" /> Reaction occurred</label>
            </div>
            <template v-if="tx.reaction_occurred == 1">
              <div class="col-6"><label class="hms-label">Reaction type</label>
                <select v-model="tx.reaction_type" class="hms-input">
                  <option value="febrile">Febrile non-haemolytic</option>
                  <option value="allergic">Allergic / urticaria</option>
                  <option value="hemolytic_acute">Acute haemolytic</option>
                  <option value="hemolytic_delayed">Delayed haemolytic</option>
                  <option value="TRALI">TRALI</option>
                  <option value="TACO">TACO (circulatory overload)</option>
                  <option value="sepsis">Bacterial / sepsis</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div class="col-6"><label class="hms-label">Severity</label>
                <select v-model="tx.reaction_severity" class="hms-input">
                  <option value="mild">Mild</option><option value="moderate">Moderate</option>
                  <option value="severe">Severe</option><option value="life_threatening">Life-threatening</option>
                </select>
              </div>
              <div class="col-12"><label class="hms-label">Action taken</label><textarea v-model="tx.reaction_action" class="hms-input" rows="2"></textarea></div>
            </template>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="tx.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="txModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveTransfusion">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "BloodBank",
  components: { Navigation },
  data() {
    return {
      tab: 'stock', saving: false,
      bloodGroups: ['O+','O-','A+','A-','B+','B-','AB+','AB-'],
      thresholds: { groups: [], low_count: 0 },
      savingGroup: '',
      stockStats: {}, requestStats: {}, logStats: {},

      units: [], byGroup: [],
      stockStatusFilter: '', stockGroupFilter: '',

      donors: [], donorSearch: '', donorGroupFilter: '',

      requests: [],
      issues: [], reactionsOnly: false,

      donorModal: false, donor: this.blankDonor(),
      donationModal: false, donation: this.blankDonation(),

      screenModal: false, screenUnitRow: {},
      screenForm: {},
      screenTests: [
        { key: 'hiv_result',      label: 'HIV' },
        { key: 'hbsag_result',    label: 'HBsAg' },
        { key: 'hcv_result',      label: 'HCV' },
        { key: 'syphilis_result', label: 'Syphilis' },
        { key: 'malaria_result',  label: 'Malaria' }
      ],

      requestModal: false, request: this.blankRequest(),

      xmModal: false, xmRequest: {}, xmRecipientGroup: '', xmCompatible: [], xmResults: {},

      txModal: false, tx: this.blankTx()
    };
  },
  computed: {
    groupMatrix() {
      const groups = ['O+','O-','A+','A-','B+','B-','AB+','AB-'];
      return groups.map(g => {
        const slice = this.byGroup.filter(b => b.blood_group === g);
        const result = { group: g, quarantine: 0, expiring: 0 };
        ['whole_blood','prbc','ffp','platelets','cryoprecipitate'].forEach(p => {
          const r = slice.find(s => s.product_type === p);
          result[p] = r ? parseInt(r.available_count || 0) : 0;
          if (r) {
            result.quarantine += parseInt(r.quarantine_count || 0);
            result.expiring   += parseInt(r.expiring_7d || 0);
          }
        });
        return result;
      });
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankDonor() { return { donor_id: null, first_name: '', last_name: '', national_id: '', date_of_birth: '', sex: '', phone: '', address: '', blood_group: 'unknown', donor_type: 'voluntary', weight_kg: null, hb_level: null, deferral_status: 'none', deferral_until: '', deferral_reason: '', notes: '' }; },
    blankDonation() { return { donor_id: null, donor_name: '', donor_group: '', donor_search: '', blood_group: 'O+', product_type: 'whole_blood', volume_ml: 450, storage_location: '' }; },
    blankRequest() { return { patient_id: null, patient_name: '', patient_search: '', admission_id: '', ward_id: '', blood_group_required: '', patient_blood_group: 'unknown', product_type: 'whole_blood', units_required: 1, urgency: 'routine', clinical_indication: '', hb_level: null }; },
    blankTx() { return { issue_id: null, unit_number: '', started_at: '', completed_at: '', volume_transfused_ml: null, reaction_occurred: 0, reaction_type: 'febrile', reaction_severity: 'mild', reaction_action: '', notes: '' }; },

    async loadAll() {
      await Promise.all([this.loadStock(), this.loadDonors(), this.loadRequests(), this.loadLog(), this.loadThresholds()]);
    },
    async loadThresholds() {
      try {
        const r = await executeJson({ function: 'getBloodStockThresholds' }, hms);
        if (r.data && r.data.success) this.thresholds = r.data.data || { groups: [], low_count: 0 };
      } catch (e) { /* non-fatal */ }
    },
    thresholdBadge(s) { return { ok: 'hms-badge-success', low: 'hms-badge-warning', critical: 'hms-badge-danger' }[s] || 'hms-badge-secondary'; },
    async saveThreshold(g) {
      this.savingGroup = g.blood_group;
      try {
        const r = await executeJson({ function: 'setBloodGroupThreshold', blood_group: g.blood_group, min_units: g.min_units, alert_emails: g.alert_emails || '' }, hms);
        if (r.data && r.data.success) { await this.loadThresholds(); }
        else if (typeof alert === 'function') alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { /* ignore */ }
      this.savingGroup = '';
    },
    async loadStock() {
      try {
        const r = await executeJson({ function: 'getBloodStock', status: this.stockStatusFilter, blood_group: this.stockGroupFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.units = d.Units || []; this.byGroup = d.ByGroup || []; this.stockStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadDonors() {
      try {
        const r = await executeJson({ function: 'getBloodDonors', search: this.donorSearch, blood_group: this.donorGroupFilter }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.donors = d.Donors || []; }
      } catch (e) { console.error(e); }
    },
    async loadRequests() {
      try {
        const r = await executeJson({ function: 'getBloodRequests' }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.requests = d.Requests || []; this.requestStats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },
    async loadLog() {
      try {
        const r = await executeJson({ function: 'getTransfusionLog', reactions_only: this.reactionsOnly ? 1 : 0 }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.issues = d.Issues || []; this.logStats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },

    // -------- DONOR --------
    openDonorModal(d) { this.donor = d ? { ...this.blankDonor(), ...d } : this.blankDonor(); this.donorModal = true; },
    async saveDonor() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveBloodDonor', ...this.donor }, hms);
        if (r.data && r.data.success) { this.donorModal = false; await this.loadDonors(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // -------- DONATION --------
    openDonationModal(donor) {
      this.donation = this.blankDonation();
      if (donor) {
        this.donation.donor_id = donor.donor_id;
        this.donation.donor_name = donor.first_name + ' ' + donor.last_name;
        this.donation.donor_group = donor.blood_group;
        if (donor.blood_group && donor.blood_group !== 'unknown') this.donation.blood_group = donor.blood_group;
      }
      this.donationModal = true;
    },
    async findDonationDonor() {
      const q = (this.donation.donor_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getBloodDonors', search: q }, hms);
      if (r.data && r.data.success) {
        const d = (r.data.data && r.data.data.Donors) || [];
        if (!d.length) { alert('No donor found.'); return; }
        const donor = d[0];
        if (donor.deferral_status === 'permanent') { alert('Donor is permanently deferred.'); return; }
        if (donor.deferral_status === 'temporary' && donor.deferral_until && new Date(donor.deferral_until) > new Date()) {
          alert('Donor deferred until ' + donor.deferral_until); return;
        }
        this.donation.donor_id = donor.donor_id;
        this.donation.donor_name = donor.first_name + ' ' + donor.last_name;
        this.donation.donor_group = donor.blood_group;
        if (donor.blood_group && donor.blood_group !== 'unknown') this.donation.blood_group = donor.blood_group;
      }
    },
    async saveDonation() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordDonation', ...this.donation }, hms);
        if (r.data && r.data.success) { this.donationModal = false; await this.loadStock(); await this.loadDonors(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // -------- SCREENING --------
    openScreenModal(u) {
      this.screenUnitRow = u;
      this.screenForm = { hiv_result: '', hbsag_result: '', hcv_result: '', syphilis_result: '', malaria_result: '' };
      this.screenModal = true;
    },
    async saveScreening() {
      this.saving = true;
      try {
        const params = { function: 'screenBloodUnit', unit_id: this.screenUnitRow.unit_id, ...this.screenForm };
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.screenModal = false; await this.loadStock(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async discardUnit(u) {
      const reason = prompt('Reason for discarding ' + u.unit_number + '?', '');
      if (!reason) return;
      const r = await executeJson({ function: 'discardBloodUnit', unit_id: u.unit_id, reason }, hms);
      if (r.data && r.data.success) await this.loadStock();
    },

    // -------- REQUEST --------
    openRequestModal() { this.request = this.blankRequest(); this.requestModal = true; },
    async findRequestPatient() {
      const q = (this.request.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const p = (d.Patients || d.patients || [])[0];
        if (p) { this.request.patient_id = p.patient_id; this.request.patient_name = p.first_name + ' ' + p.last_name; }
        else alert('No patient found.');
      }
    },
    async saveRequest() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'requestBlood', ...this.request }, hms);
        if (r.data && r.data.success) { this.requestModal = false; await this.loadRequests(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // -------- CROSSMATCH --------
    async openCrossmatchModal(r) {
      this.xmRequest = r; this.xmResults = {}; this.xmCompatible = []; this.xmRecipientGroup = '';
      this.xmModal = true;
      const res = await executeJson({ function: 'getCompatibleUnits', request_id: r.request_id }, hms);
      if (res.data && res.data.success) {
        const d = res.data.data || {};
        this.xmCompatible = d.CompatibleUnits || [];
        this.xmRecipientGroup = d.RecipientGroup || '';
      }
    },
    async confirmCrossmatch(u) {
      const result = this.xmResults[u.unit_id];
      if (!result) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'crossmatchUnit', request_id: this.xmRequest.request_id, unit_id: u.unit_id, crossmatch_result: result }, hms);
        if (r.data && r.data.success) {
          await Promise.all([this.openCrossmatchModal(this.xmRequest), this.loadRequests(), this.loadLog(), this.loadStock()]);
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // -------- ISSUE --------
    async issueUnit(i) {
      const recv = prompt('Received by (name of porter / nurse picking up)?', '');
      if (recv === null) return;
      const r = await executeJson({ function: 'issueBloodUnit', issue_id: i.issue_id, received_by_name: recv }, hms);
      if (r.data && r.data.success) await this.loadLog();
    },

    // -------- TRANSFUSION --------
    openTransfuseModal(i) {
      this.tx = { ...this.blankTx(), issue_id: i.issue_id, unit_number: i.unit_number };
      this.tx.started_at = new Date().toISOString().substring(0,16);
      this.txModal = true;
    },
    async saveTransfusion() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordTransfusion', ...this.tx }, hms);
        if (r.data && r.data.success) { this.txModal = false; await this.loadLog(); await this.loadRequests(); await this.loadStock(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // -------- helpers --------
    formatProduct(p) {
      return ({ whole_blood: 'Whole', prbc: 'PRBC', ffp: 'FFP', platelets: 'Platelets', cryoprecipitate: 'Cryo' })[p] || p;
    },
    statusPill(s) {
      return ({ available: 'hms-pill-success', quarantine: 'hms-pill-info', reserved: 'hms-pill-warn',
        issued: 'hms-pill-info', transfused: 'hms-pill-muted', expired: 'hms-pill-danger', discarded: 'hms-pill-danger' })[s] || '';
    },
    issueStatusPill(s) {
      return ({ crossmatched: 'hms-pill-info', issued: 'hms-pill-warn', transfused: 'hms-pill-success', reaction: 'hms-pill-danger', wasted: 'hms-pill-danger' })[s] || '';
    },
    xmPill(r) {
      return ({ compatible: 'hms-pill-success', incompatible: 'hms-pill-danger', warm: 'hms-pill-warn', cold: 'hms-pill-warn', not_done: 'hms-pill-muted' })[r] || '';
    },
    urgencyClass(u) { return ({ massive: 'st-action', emergency: 'st-action', urgent: 'st-warn' })[u] || ''; },
    urgencyPill(u)  { return ({ massive: 'hms-pill-danger', emergency: 'hms-pill-danger', urgent: 'hms-pill-warn', routine: 'hms-pill-muted' })[u] || ''; },
    rowClassForUnit(u) {
      if (u.status === 'expired' || u.status === 'discarded') return 'row-muted';
      if (u.days_to_expiry < 7) return 'row-warn';
      return '';
    },
    screenDot(r) {
      return ({ negative: 'sd-ok', positive: 'sd-bad', indeterminate: 'sd-warn', pending: 'sd-pending' })[r] || 'sd-pending';
    },
    formatDateTime(s) { if (!s) return '—'; const d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-success { color: #276749; } .hms-stat-info { color: #2c5282; }
.hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body.p-0 { padding: 0; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn  { background: #fffaf0; }
.hms-table tr.row-muted { color: #a0aec0; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-group-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; background: #edf2f7; color: #2d3748; }
.hms-group-pill.g-o   { background: #fed7d7; color: #742a2a; }
.hms-group-pill.g-a   { background: #bee3f8; color: #2a4365; }
.hms-group-pill.g-b   { background: #c6f6d5; color: #22543d; }
.hms-group-pill.g-ab  { background: #fbcfe8; color: #831843; }

.hms-screen-dot { display: inline-block; width: 18px; height: 18px; line-height: 18px; text-align: center; border-radius: 50%; font-size: 10px; font-weight: 700; margin-right: 2px; }
.sd-ok      { background: #c6f6d5; color: #22543d; }
.sd-bad     { background: #fed7d7; color: #742a2a; }
.sd-warn    { background: #feebc8; color: #7b341e; }
.sd-pending { background: #edf2f7; color: #a0aec0; }

.hms-request-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }
.hms-request-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #4299e1; border-radius: 8px; padding: 14px; }
.hms-request-card.st-warn   { border-left-color: #dd6b20; background: #fffaf0; }
.hms-request-card.st-action { border-left-color: #e53e3e; background: #fff5f5; }
.hms-request-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
.hms-request-body { font-size: 13px; }
.hms-request-actions { margin-top: 10px; display: flex; gap: 6px; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #a0aec0; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-warn-banner { background: #fffaf0; border: 1px solid #f6ad55; color: #7b341e; padding: 10px; border-radius: 6px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; font-size: 13px; }
.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 8px 10px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }
.st-bad { color: #c53030; font-weight: 600; }

.hms-muted-sm { color: #a0aec0; font-size: 11px; }
</style>
